import { useEffect, useMemo, useState } from "react";
import { Search, X, SlidersHorizontal } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { fetchCategories, fetchBrands, fetchProducts } from "@/lib/api";

const SORTS = [
    { id: "name_asc", label: "Name A → Z" },
    { id: "name_desc", label: "Name Z → A" },
    { id: "brand_asc", label: "Brand" },
    { id: "newest", label: "Newest" },
];

export default function ProductsPage() {
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [products, setProducts] = useState([]);
    const [activeCat, setActiveCat] = useState("All");
    const [activeBrand, setActiveBrand] = useState("All");
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("name_asc");
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        fetchCategories().then(setCategories).catch(() => {});
        fetchBrands().then(setBrands).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        fetchProducts({ category: activeCat, brand: activeBrand, q, sort })
            .then((data) => {
                setProducts(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [activeCat, activeBrand, q, sort]);

    const catTabs = useMemo(() => ["All", ...categories], [categories]);
    const brandTabs = useMemo(() => ["All", ...brands], [brands]);

    const activeFilters = [activeCat !== "All" && activeCat, activeBrand !== "All" && activeBrand, q && `"${q}"`].filter(Boolean);

    return (
        <div data-testid="products-page" className="bg-white">
            <section className="relative overflow-hidden bg-navy text-white">
                <div
                    className="absolute inset-0 opacity-[0.08]"
                    style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #1E90FF 0, transparent 40%), radial-gradient(circle at 80% 70%, #1E90FF 0, transparent 40%)" }}
                />
                <div className="container-lg relative py-20 lg:py-24">
                    <span className="text-xs font-bold tracking-[0.24em] uppercase text-brand">Catalog</span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
                        100+ enterprise networking<br />products, in stock.
                    </h1>
                    <p className="mt-4 text-slate-300 max-w-2xl">
                        Browse core switches, routers, firewalls, wireless, servers, NVRs, structured cabling and more
                        from every major enterprise brand. Submit inquiries for instant quotations.
                    </p>
                </div>
            </section>

            <section className="container-lg py-8 lg:py-12">
                <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6">
                    <div className="relative flex-1 max-w-xl">
                        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="products-search-input"
                            placeholder="Search products, brands, model numbers, specs…"
                            className="w-full pl-10 pr-4 py-3 rounded-full border border-slate-200 bg-white text-sm text-navy focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-colors"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="hidden sm:flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                            Sort
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                data-testid="products-sort-select"
                                className="ml-1 rounded-full border border-slate-200 bg-white text-sm text-navy pl-3 pr-8 py-2 font-medium normal-case tracking-normal focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none"
                            >
                                {SORTS.map((s) => (
                                    <option key={s.id} value={s.id}>{s.label}</option>
                                ))}
                            </select>
                        </label>
                        <button
                            type="button"
                            onClick={() => setShowFilters((v) => !v)}
                            data-testid="products-filters-toggle"
                            className="lg:hidden inline-flex items-center gap-2 rounded-full bg-navy text-white px-4 py-2.5 text-sm font-semibold"
                        >
                            <SlidersHorizontal className="w-4 h-4" /> Filters
                        </button>
                    </div>
                </div>

                <div className="text-sm text-slate-600 mb-4" data-testid="products-count">
                    {loading ? "Loading…" : `${products.length} product${products.length === 1 ? "" : "s"}`}
                    {activeFilters.length > 0 && (
                        <span className="ml-2 text-slate-400">· filtered by {activeFilters.join(" + ")}</span>
                    )}
                    {activeFilters.length > 0 && (
                        <button
                            type="button"
                            onClick={() => { setActiveCat("All"); setActiveBrand("All"); setQ(""); }}
                            data-testid="products-clear-filters"
                            className="ml-3 inline-flex items-center gap-1 text-brand hover:text-brand-hover font-semibold"
                        >
                            <X className="w-3 h-3" /> Clear
                        </button>
                    )}
                </div>

                <div className="grid lg:grid-cols-[260px_1fr] gap-8">
                    <aside className={`${showFilters ? "block" : "hidden"} lg:block lg:sticky lg:top-32 self-start space-y-6`}>
                        <div>
                            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Categories</div>
                            <div className="flex flex-wrap lg:flex-col gap-1.5">
                                {catTabs.map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => setActiveCat(c)}
                                        data-testid={`category-tab-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className={`whitespace-normal text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors leading-snug ${
                                            activeCat === c
                                                ? "bg-navy text-white"
                                                : "bg-white text-slate-700 hover:bg-brand-soft hover:text-navy border border-slate-200"
                                        }`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Brands</div>
                            <div className="flex flex-wrap lg:flex-col gap-1.5">
                                {brandTabs.map((b) => (
                                    <button
                                        key={b}
                                        onClick={() => setActiveBrand(b)}
                                        data-testid={`brand-tab-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className={`whitespace-normal text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors leading-snug ${
                                            activeBrand === b
                                                ? "bg-brand text-white"
                                                : "bg-white text-slate-700 hover:bg-brand-soft hover:text-navy border border-slate-200"
                                        }`}
                                    >
                                        {b}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </aside>

                    <div>
                        {loading ? (
                            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 aspect-[4/5] animate-pulse" />
                                ))}
                            </div>
                        ) : products.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="products-empty">
                                No products found. Try a different category, brand, or search term.
                            </div>
                        ) : (
                            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                {products.map((p) => (
                                    <ProductCard key={p.id} product={p} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
